import React, { useMemo, useState, useEffect } from 'react';
import { calcularSueldoEfectivoTramos } from '../utils/sueldoTramosPlame';

function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return '—';
  try {
    const [year, month, day] = String(dateString).split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

/**
 * Modal: sueldo PLAME distinto a partir de un día (hasta el X un monto, el resto otro).
 */
const SueldoTramosPlameModal = ({
  isOpen,
  onClose,
  empleados = [],
  tramosMap = {},
  fechaInicioPlanilla = null,
  fechaFinPlanilla = null,
  planillaAlternativaActiva = false,
  onGuardar,
  onRecalcular,
  guardando = false,
  recalculando = false,
}) => {
  const empleadosLista = useMemo(
    () =>
      (empleados || []).filter((emp) => {
        const tipo = String(emp?.tipo_contratacion || emp?.datosEmpleado?.tipo_contratacion || '')
          .toLowerCase();
        if (tipo.includes('honorario') || tipo === 'rh') return false;
        return true;
      }),
    [empleados]
  );

  const [empleadoId, setEmpleadoId] = useState(null);
  const [activo, setActivo] = useState(false);
  const [fechaCambio, setFechaCambio] = useState('');
  const [sueldo1, setSueldo1] = useState('');
  const [sueldo2, setSueldo2] = useState('');
  const [destino, setDestino] = useState('plame');

  const iniPlanilla = fechaInicioPlanilla ? String(fechaInicioPlanilla).split('T')[0] : '';
  const finPlanilla = fechaFinPlanilla ? String(fechaFinPlanilla).split('T')[0] : '';

  useEffect(() => {
    if (!isOpen) return;
    if (empleadosLista.length > 0 && !empleadoId) {
      setEmpleadoId(empleadosLista[0].id);
    }
  }, [isOpen, empleadosLista, empleadoId]);

  useEffect(() => {
    if (!empleadoId) return;
    const cfg = tramosMap[String(empleadoId)];
    const emp = empleadosLista.find((e) => Number(e.id) === Number(empleadoId));
    const tipoPlanilla = String(emp?.tipo_planilla || emp?.datosEmpleado?.tipo_planilla || '').toLowerCase();
    const sueldoDefPlame = emp?.sueldo_basico != null ? String(emp.sueldo_basico) : '';
    const sueldoDefInterna = emp?.sueldo_interno != null ? String(emp.sueldo_interno) : sueldoDefPlame;
    const destinoDef = tipoPlanilla === 'solo_interna' ? 'interna' : 'plame';
    if (cfg?.activo) {
      setActivo(true);
      setFechaCambio(cfg.fecha_cambio || iniPlanilla);
      const dest = cfg.destino || destinoDef;
      setDestino(dest);
      const sueldoDef = dest === 'interna' ? sueldoDefInterna : sueldoDefPlame;
      setSueldo1(cfg.sueldo_1 != null ? String(cfg.sueldo_1) : sueldoDef);
      setSueldo2(cfg.sueldo_2 != null ? String(cfg.sueldo_2) : sueldoDef);
    } else {
      setActivo(false);
      setFechaCambio(iniPlanilla);
      setDestino(destinoDef);
      const sueldoDef = destinoDef === 'interna' ? sueldoDefInterna : sueldoDefPlame;
      setSueldo1(sueldoDef);
      setSueldo2(sueldoDef);
    }
  }, [empleadoId, tramosMap, empleadosLista, iniPlanilla]);

  const preview = useMemo(() => {
    if (!activo || !fechaCambio) return null;
    const s1 = parseFloat(sueldo1);
    const s2 = parseFloat(sueldo2);
    if (!Number.isFinite(s1) || !Number.isFinite(s2)) return null;
    return calcularSueldoEfectivoTramos(
      { fecha_cambio: fechaCambio, sueldo_1: s1, sueldo_2: s2 },
      { fechaInicio: iniPlanilla, fechaFin: finPlanilla, tipo: 'mes_unico', mes: { diasCompletosDelMes: 30 } },
      {}
    );
  }, [activo, fechaCambio, sueldo1, sueldo2, iniPlanilla, finPlanilla]);

  const handleGuardar = async () => {
    if (!empleadoId) return;
    if (activo) {
      if (!fechaCambio) {
        alert('Indica el primer día del sueldo nuevo.');
        return;
      }
      if (iniPlanilla && fechaCambio < iniPlanilla) {
        alert('La fecha de cambio no puede ser anterior al inicio de la planilla.');
        return;
      }
      if (finPlanilla && fechaCambio > finPlanilla) {
        alert('La fecha de cambio no puede ser posterior al fin de la planilla.');
        return;
      }
      const s1 = parseFloat(sueldo1);
      const s2 = parseFloat(sueldo2);
      if (!Number.isFinite(s1) || s1 < 0 || !Number.isFinite(s2) || s2 < 0) {
        alert('Ingresa sueldos válidos para ambos tramos.');
        return;
      }
      const emp = empleadosLista.find((e) => Number(e.id) === Number(empleadoId));
      const tipoPlanilla = String(emp?.tipo_planilla || emp?.datosEmpleado?.tipo_planilla || '').toLowerCase();
      let dest = planillaAlternativaActiva ? destino : 'plame';
      if (tipoPlanilla === 'solo_interna') dest = dest === 'plame' ? 'interna' : dest;
      await onGuardar({
        empleados: [
          {
            id: Number(empleadoId),
            activo: true,
            fecha_cambio: fechaCambio,
            sueldo_1: s1,
            sueldo_2: s2,
            destino: dest,
          },
        ],
      });
    } else {
      await onGuardar({
        empleados: [{ id: Number(empleadoId), activo: false }],
        eliminar: [Number(empleadoId)],
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Cambio de sueldo a partir de un día</h2>
            <p className="text-emerald-100 text-sm">
              Hasta el día anterior un monto; desde esa fecha el resto del mes con otro.
              Puedes aplicarlo a PLAME, a la planilla alternativa, o a ambas.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg px-3 py-1 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {empleadosLista.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No hay personal de planilla en este período.
            </p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={empleadoId || ''}
                  onChange={(e) => setEmpleadoId(Number(e.target.value) || null)}
                >
                  {empleadosLista.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre_completo || emp.nombre || `Empleado ${emp.id}`}
                      {emp.documento || emp.numero_documento
                        ? ` (${emp.documento || emp.numero_documento})`
                        : ''}
                      {tramosMap[String(emp.id)]?.activo ? ' · activo' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={activo}
                  onChange={(e) => setActivo(e.target.checked)}
                />
                Activar cambio de sueldo a mitad de período
              </label>

              {activo && (
                <>
                  {planillaAlternativaActiva && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aplicar a
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { id: 'plame', label: 'PLAME' },
                          { id: 'interna', label: 'Planilla alternativa' },
                          { id: 'ambas', label: 'Ambas' },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setDestino(opt.id)}
                            className={`px-3 py-1.5 text-sm rounded-lg border ${
                              destino === opt.id
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primer día del sueldo nuevo
                    </label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={fechaCambio}
                      min={iniPlanilla || undefined}
                      max={finPlanilla || undefined}
                      onChange={(e) => setFechaCambio(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ejemplo: si hasta el 8 cobró S/ 1,500 y desde el 9 S/ 2,000, elige el 09 del mes.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sueldo hasta el día anterior
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={sueldo1}
                        onChange={(e) => setSueldo1(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sueldo desde esa fecha
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={sueldo2}
                        onChange={(e) => setSueldo2(e.target.value)}
                      />
                    </div>
                  </div>

                  {preview && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 space-y-1">
                      <p>
                        <strong>{preview.dias1}</strong> día(s) a S/{' '}
                        {Number(preview.sueldo_1).toFixed(2)} = S/ {Number(preview.monto1).toFixed(2)}
                      </p>
                      <p>
                        <strong>{preview.dias2}</strong> día(s) a S/{' '}
                        {Number(preview.sueldo_2).toFixed(2)} = S/ {Number(preview.monto2).toFixed(2)}
                      </p>
                      <p>
                        Sueldo ponderado (base 0121): S/ {Number(preview.sueldoEfectivo).toFixed(2)}
                      </p>
                      <p className="font-semibold">
                        0121 estimado del mes: S/ {Number(preview.monto0121).toFixed(2)}
                      </p>
                      <p className="text-xs text-emerald-700">
                        Se aplica a:{' '}
                        {destino === 'interna'
                          ? 'planilla alternativa'
                          : destino === 'ambas'
                            ? 'PLAME y planilla alternativa'
                            : 'PLAME'}
                      </p>
                      <p className="text-xs text-emerald-700">
                        Período: {formatDateToDDMMYYYY(iniPlanilla)} – {formatDateToDDMMYYYY(finPlanilla)}
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
            disabled={guardando || recalculando}
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={async () => {
              onClose();
              if (onRecalcular) await onRecalcular();
            }}
            disabled={guardando || recalculando}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {recalculando ? 'Recalculando…' : 'Salir y recalcular'}
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || recalculando || empleadosLista.length === 0}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar y recalcular'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SueldoTramosPlameModal;
